import type { Entry } from '@/document'

// Entry with resolved topics (from useLibraryData)
interface EntryWithTopics extends Entry {
  topics?: Array<{ id: string; name: string }>
}

/**
 * Get topic names from an entry.
 * Prefers resolved topics (from EntryWithRelations), falls back to metadata.genres for legacy data.
 */
function getTopicNames(entry: EntryWithTopics): string[] {
  // Prefer resolved topics from topicIds (populated by useLibraryData)
  if (entry.topics && entry.topics.length > 0) {
    return entry.topics.map((t) => t.name)
  }
  // Fallback: legacy metadata.genres for old entries not yet migrated
  const legacyGenres = entry.metadata.genres as string[] | undefined
  return legacyGenres ?? []
}

// Filter types
export interface MovieFilterState {
  status: string[]
  genres: string[]
}

export interface BookFilterState {
  status: string[]
}

export interface MusicFilterState {
  type: ('artist' | 'label' | 'release')[]
  genres: string[]
}

// Filter functions
export function filterMovies(
  movies: Entry[],
  filters: MovieFilterState,
): Entry[] {
  return movies.filter((movie) => {
    if (
      filters.status.length > 0 &&
      !filters.status.includes(movie.metadata.status as string)
    ) {
      return false
    }
    if (filters.genres.length > 0) {
      const topicNames = getTopicNames(movie)
      if (
        topicNames.length === 0 ||
        !filters.genres.some((g) => topicNames.includes(g))
      ) {
        return false
      }
    }
    return true
  })
}

export function filterBooks(books: Entry[], filters: BookFilterState): Entry[] {
  return books.filter((book) => {
    if (
      filters.status.length > 0 &&
      !filters.status.includes(book.metadata.status as string)
    ) {
      return false
    }
    return true
  })
}

export function filterMusic(
  artists: Entry[],
  labels: Entry[],
  filters: MusicFilterState,
): { artists: Entry[]; labels: Entry[] } {
  let filteredArtists = artists
  let filteredLabels = labels

  if (filters.type.length > 0) {
    if (!filters.type.includes('artist')) filteredArtists = []
    if (!filters.type.includes('label')) filteredLabels = []
  }

  if (filters.genres.length > 0) {
    filteredArtists = filteredArtists.filter((a) => {
      const topicNames = getTopicNames(a)
      return (
        topicNames.length > 0 &&
        filters.genres.some((g) => topicNames.includes(g))
      )
    })
    filteredLabels = filteredLabels.filter((l) => {
      const topicNames = getTopicNames(l)
      return (
        topicNames.length > 0 &&
        filters.genres.some((g) => topicNames.includes(g))
      )
    })
  }

  return {
    artists: filteredArtists,
    labels: filteredLabels,
  }
}

// Search utilities
export function searchMovies(movies: Entry[], query: string): Entry[] {
  if (!query.trim()) return movies
  const q = query.toLowerCase()
  return movies.filter((m) => {
    const director = m.metadata.director as string | undefined
    return (
      m.title.toLowerCase().includes(q) || director?.toLowerCase().includes(q)
    )
  })
}

export function searchBooks(books: Entry[], query: string): Entry[] {
  if (!query.trim()) return books
  const q = query.toLowerCase()
  return books.filter((b) => {
    const author = b.metadata.author as string | undefined
    return (
      b.title.toLowerCase().includes(q) || author?.toLowerCase().includes(q)
    )
  })
}

export function searchArtists(artists: Entry[], query: string): Entry[] {
  if (!query.trim()) return artists
  const q = query.toLowerCase()
  return artists.filter((a) => a.title.toLowerCase().includes(q))
}

export function searchLabels(labels: Entry[], query: string): Entry[] {
  if (!query.trim()) return labels
  const q = query.toLowerCase()
  return labels.filter((l) => l.title.toLowerCase().includes(q))
}

// Extract filter options from data
export function getMovieStatusOptions(movies: Entry[]) {
  const labels: Record<string, string> = {
    seen: 'Watched',
    want_to_watch: 'Watchlist',
  }
  return Array.from(
    new Set(movies.map((m) => m.metadata.status as string)),
  ).map((s) => ({
    value: s,
    label: labels[s] || s,
  }))
}

export function getMovieGenreOptions(movies: Entry[]) {
  const genres = new Set<string>()
  movies.forEach((m) => getTopicNames(m).forEach((g) => genres.add(g)))
  return Array.from(genres)
    .sort()
    .map((g) => ({ value: g, label: g }))
}

export function getBookStatusOptions(books: Entry[]) {
  const labels: Record<string, string> = {
    read: 'Read',
    reading: 'Reading',
    want_to_read: 'To Read',
  }
  return Array.from(new Set(books.map((b) => b.metadata.status as string))).map(
    (s) => ({
      value: s,
      label: labels[s] || s,
    }),
  )
}

export function getMusicGenreOptions(artists: Entry[], labels: Entry[]) {
  const genres = new Set<string>()
  // Extract from artists
  artists.forEach((a) => getTopicNames(a).forEach((g) => genres.add(g)))
  // Extract from labels
  labels.forEach((l) => getTopicNames(l).forEach((g) => genres.add(g)))
  return Array.from(genres)
    .sort()
    .map((g) => ({ value: g, label: g }))
}
